import { Component, OnInit, ViewChild } from '@angular/core';
import { SharedService } from '../../services/shared.service';
import { PanelBarExpandMode } from '@progress/kendo-angular-layout';
import { IndicationGroup } from '@app/models/indication';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { SearchIndicationsModel } from '@app/models/search-indications';
import { SelectionsErrorModel } from '@app/models/selections-model';
import { VennHealthUtils } from '@app/shared/vennHealthUtils';
import { checkboxCheckedIcon, checkboxIcon } from '@progress/kendo-svg-icons';
import { AccountService } from '@app/services/account-service';

@Component({
  selector: 'app-indication-filters',
  templateUrl: './indication-filters.component.html',
  styleUrls: ['./indication-filters.component.css']
})
export class IndicationFiltersComponent implements OnInit {

  public checkboxIcon = checkboxIcon;
  public checkboxCheckedIcon = checkboxCheckedIcon;

  @ViewChild("searchPanelbar", { static: false}) panelBar;

  expandMode = PanelBarExpandMode.Default;

  allIndications: IndicationGroup[] = [];
  validationErrors: SelectionsErrorModel[] = [];
  groupedValidationErrors = [];

  showSelectedFiltersPanel = false;
  selectedSearchIndication: SearchIndicationsModel;

  isDemoUser = false;

  constructor(
    private readonly sharedService: SharedService,
    private readonly accountService: AccountService
  ) { }

  ngOnInit(): void {

    const account = this.accountService.accountValue;
    if (account) {
      this.isDemoUser = account.roles.includes(103);
    }

    this.sharedService.showFiltersPanelSubscription.subscribe(i => this.showSelectedFiltersPanel = i);
    // sets up all indications for the first time
    const originalAllIndications = JSON.parse(JSON.stringify(vennHealthConstants.allIndications)).sort((a,b) => a.order - b.order);
    
    this.sharedService.setAllIndications(originalAllIndications);

    this.sharedService.allIndications.subscribe(data => {
      if (data) {
        this.allIndications = Object.assign([], data.sort((a,b) => a.order - b.order));
      }
    });

    this.sharedService.runQuery.subscribe(
      run => {
        if (run !== null && run === true) {
          // collapse and deselect all panels
          this.closeAllPanels();
        }
      }
    );

    this.sharedService.closeAllPanels.subscribe(
      i => {
        if (i && i === true) {
          this.closeAllPanels();
        }
      }
    );

    this.sharedService.selectedSearchIndication.subscribe(selection => {
      if (selection) {
        this.closeAllPanels();
        const matchingPanel = this.panelBar.contentChildItems.find((i) => i.title === selection.sectionName);
        if (matchingPanel) {
          matchingPanel.expanded = true;
          matchingPanel.selected = true;
        }
        this.selectedSearchIndication = selection;
      }
    });

    this.sharedService.validationErrors.subscribe(data => {
      if (data) {
        this.validationErrors = data;
        if (this.validationErrors.length > 0) {
          this.groupedValidationErrors = VennHealthUtils.formatGroupedByIntoKVPArray(this.validationErrors, 'indicationGroupDisplayName');
          const indicationGroupsWithErrors = this.groupedValidationErrors.map(i => i.key);
          this.openPanels(indicationGroupsWithErrors);
        }
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        this.closeAllPanels();
        const loadedIndications: IndicationGroup[] = data.indicationGroups;
        let panelsToOpen = [];
        loadedIndications.forEach(group => {
          if (group.indications.some(i => i.selected)) {
            panelsToOpen.push(group.displayName);
          }
        });
        this.openPanels(panelsToOpen);
      }
    });
  }

  getCheckedIcon(indicationGroup: IndicationGroup): boolean {
    if (indicationGroup && indicationGroup.indications.some(i => i.selected === true)) {
      return true;
    }
    return false;
  }

  getUncheckedIcon(indicationGroup: IndicationGroup): boolean {
    if (indicationGroup && indicationGroup.indications.some(i => i.selected === true)) {
      return false;
    }
    return true;
  }

  getSelectedIndicationsInGroupCount(indicationGroup: IndicationGroup): string {
    const count = indicationGroup.indications.filter(i => i.selected === true).length ?? 0;
    return count.toString();
  }

  hasReachedDemoSelectionLimit(): boolean {
    const selectedIndicationsCount = this.getSelectedIndicationsCount();
    if (this.isDemoUser && selectedIndicationsCount > 2) {
      return true;
    }
    return false;
  }

  hasReachedSelectionLimit(): boolean {
    const selectedIndicationsCount = this.getSelectedIndicationsCount();
    if (selectedIndicationsCount > 5) {
      return true;
    }
    return false;
  }

  selectionLimitMessage(): string {
    let message = `You have selected ${this.getSelectedIndicationsCount()} indications which exceeds the limit of 5 per query. Please reduce your selected indications to 5 or fewer.`;
    if (this.isDemoUser) {
      message = `You have selected ${this.getSelectedIndicationsCount()} indications which exceeds the limit of 2 per demo query. Please reduce your selected indications to 2 or fewer.`;
    }
    return message;
  }

  getSelectedIndicationsCount(): number {
    var count = 0;
    this.allIndications.forEach(group => {
      group.indications.forEach(indication => {
        if (indication.selected) {
          count++;
        }
      });
    })

    return count;
  }

  private openPanels(groupNames: string[]): void {
      if (this.panelBar) {
        this.panelBar.contentChildItems.forEach((el) => {
          if (groupNames.includes(el.title)) {
            el.expanded = true;
            // el.selected = true;
          }
        });
      }
  }

  private closeAllPanels(): void {
    if (this.panelBar) {
      this.panelBar.contentChildItems.forEach((el) => {
        el.selected = false;
        el.expanded = false;
      });
    }
  }
}
